import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { PhotoListFormFieldItem } from '../containers/PhotoListFormField';
import { ImageRequireSource, ImageResizeMode, ImageURISource, StyleSheet, TouchableOpacity, View } from 'react-native';
import { MediasInfos, SplitMedia } from '../../utils/Medias';
import { getContributionImageSource, getDocumentURI, getPictureURI } from '../../services/api/helper.api';
import Pdf, { PdfRef } from '../atomic/Pdf';
import { isMobile, PickPhotoOrDocumentResult } from '../../services/utils';
import Media from '../containers/Media';
import { BACKGROUND_COLOR, DARK_GRAY, PRIMARY_COLOR, WHITE } from '../../styles/appColor';
import FontAwesome6 from 'react-native-vector-icons/FontAwesome6';
import Carousel, { CarouselProps } from '../atomic/Carousel';
import { AppModalOverlay } from '../atomic/AppModalOverlay';
import { CloseButton } from '../atomic/CloseButton';
import { ContributionType } from '../../types';
import Animated, { SlideInRight, SlideInLeft } from 'react-native-reanimated';
import Image from '../atomic/Image';
import ZoomableWeb from '../atomic/ZoomableWeb';

export type ContributionPhotoListProps = Omit<CarouselProps<PhotoListFormFieldItem>, 'renderItem' | 'data'> & {
  photos: PhotoListFormFieldItem[];
  noInteractions?: boolean;
  noBorderRadius?: boolean;
  contributionType: ContributionType;
  renderActions?: (props: { index: number; item: PhotoListFormFieldItem }) => ReactNode;
  onAddPressed?: () => void;
};

export type ContributionPhotoListItemProps = {
  item: PhotoListFormFieldItem;
  width: number;
  height: number;
  index: number;
  selected: boolean;
  noInteractions: boolean;
  noBorderRadius: boolean;
  setZoomedIndex: (index: number) => void;
};

export function ContributionPhotoList({
  photos,
  contributionType,
  noInteractions = false,
  noBorderRadius = false,
  renderActions,
  onAddPressed,
  ...rest
}: ContributionPhotoListProps) {
  const [zoomedIndex, setZoomedIndex] = useState<number | undefined>(undefined);
  const [lastZoomedIndex, setLastZoomedIndex] = useState<number | undefined>(undefined);
  const [modalDimensions, setModalDimensions] = useState<{ width: number; height: number } | undefined>(undefined);
  const [itemDimensions, setItemDimensions] = useState<{ width: number; height: number } | undefined>(undefined);
  const contributionImagesSource = getContributionImageSource(undefined, contributionType);

  useEffect(() => {
    setTimeout(() => {
      setLastZoomedIndex(zoomedIndex);
    }, 500);
  }, [zoomedIndex]);

  return (
    <View>
      <Carousel
        {...rest}
        data={photos || []}
        renderItem={({ item, index, selected, width, height }) => {
          return (
            <View key={item.id || item.videoUrl || item.file?.toString()} style={styles.itemContainer}>
              <ContributionPhotoListItem
                item={item}
                width={width}
                height={height}
                index={index}
                selected={selected}
                noInteractions={noInteractions}
                noBorderRadius={noBorderRadius}
                setZoomedIndex={(index) => {
                  setZoomedIndex(index);
                  setLastZoomedIndex(index);
                }}
              />
              {renderActions && selected && renderActions({ index, item })}
            </View>
          );
        }}
        extraItems={
          onAddPressed
            ? [
                <View style={styles.addContainer}>
                  <TouchableOpacity onPress={onAddPressed}>
                    <FontAwesome6 size={72} name="circle-plus" color={PRIMARY_COLOR} />
                  </TouchableOpacity>
                </View>,
              ]
            : undefined
        }
        renderEmptyComponent={({ width, height }) => (
          <Image
            source={contributionImagesSource}
            style={[styles.defaultImage, noBorderRadius ? {} : styles.borderRadius, { width, height }]}
            resizeMode="cover"
          />
        )}
      />
      <AppModalOverlay
        isVisible={zoomedIndex !== undefined}
        onBackdropPress={() => {
          setZoomedIndex(undefined);
          setLastZoomedIndex(undefined);
        }}
        overlayStyle={styles.modal}
      >
        {zoomedIndex !== undefined && (
          <View
            style={styles.modalContainer}
            onLayout={(event) => {
              const { width, height } = event.nativeEvent.layout;
              setModalDimensions({ width, height });
            }}
          >
            <View
              style={[styles.modalItemContainer, { width: modalDimensions?.width, height: modalDimensions?.width }]}
            >
              <View style={styles.modalItemLayer} onLayout={(event) => setItemDimensions(event.nativeEvent.layout)}>
                {lastZoomedIndex !== undefined && lastZoomedIndex !== zoomedIndex && itemDimensions && (
                  <View style={styles.modalItemSubcontainer}>
                    <ContributionPhotoListItemZoomed
                      item={photos[lastZoomedIndex]}
                      index={lastZoomedIndex}
                      width={itemDimensions.width}
                      height={itemDimensions.width}
                      selected={true}
                    />
                  </View>
                )}
                {zoomedIndex !== undefined && lastZoomedIndex !== undefined && itemDimensions && (
                  <Animated.View
                    style={styles.modalItemLayer}
                    key={zoomedIndex}
                    entering={
                      lastZoomedIndex === zoomedIndex
                        ? undefined
                        : lastZoomedIndex < zoomedIndex
                        ? SlideInRight
                        : SlideInLeft
                    }
                  >
                    <View style={styles.modalItemSubcontainer}>
                      <ContributionPhotoListItemZoomed
                        item={photos[zoomedIndex]}
                        index={zoomedIndex}
                        width={itemDimensions.width}
                        height={itemDimensions.width}
                        selected={true}
                      />
                    </View>
                  </Animated.View>
                )}
                {itemDimensions && photos.length > 1 && (
                  <>
                    <TouchableOpacity
                      onPress={() => setZoomedIndex((i) => (i !== undefined ? i - 1 : undefined))}
                      style={[
                        styles.prevButton,
                        { top: (itemDimensions.height - 30) / 2 },
                        styles.insideButton,
                        zoomedIndex === 0 ? styles.disabled : undefined,
                      ]}
                      disabled={zoomedIndex === 0}
                    >
                      <FontAwesome6 name="chevron-left" color={DARK_GRAY} size={30} />
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() => setZoomedIndex((i) => (i !== undefined ? i + 1 : undefined))}
                      style={[
                        styles.nextButton,
                        { top: (itemDimensions.height - 30) / 2 },
                        styles.insideButton,
                        zoomedIndex === photos.length - 1 ? styles.disabled : undefined,
                      ]}
                      disabled={zoomedIndex === photos.length - 1}
                    >
                      <FontAwesome6 name="chevron-right" color={DARK_GRAY} size={30} />
                    </TouchableOpacity>
                  </>
                )}
              </View>
            </View>
            <CloseButton
              onPress={() => {
                setZoomedIndex(undefined);
                setLastZoomedIndex(undefined);
              }}
            />
          </View>
        )}
      </AppModalOverlay>
    </View>
  );
}

export function ContributionPhotoListItem({
  index,
  item,
  width,
  height,
  selected,
  noInteractions,
  noBorderRadius,
  setZoomedIndex,
}: ContributionPhotoListItemProps) {
  const pdfRef = useRef<PdfRef>(null);
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfTotalPage, setPdfTotalPage] = useState(1);

  if (item.videoUrl) {
    if (noInteractions || !selected) {
      for (const mediaName in MediasInfos) {
        const [, url] = SplitMedia(item.videoUrl);
        const mediaInfo = MediasInfos[mediaName];
        if (mediaInfo.isMedia(url)) {
          const mediaLogo = mediaInfo.logo;

          return (
            <View style={[styles.image, noBorderRadius ? {} : styles.borderRadius]}>
              <Image source={{ uri: mediaInfo.imagePreview(url) }} style={styles.image} />
              {!!mediaLogo && <Image source={mediaLogo as ImageRequireSource} style={styles.mediaLogo} />}
            </View>
          );
        }
      }

      return <View />;
    } else {
      return (
        <View style={[styles.video, noBorderRadius ? {} : styles.borderRadius]}>
          <Media media={item.videoUrl} width={width} height={height} />
        </View>
      );
    }
  } else if (
    item.file &&
    typeof item.file === 'object' &&
    (item.file as PickPhotoOrDocumentResult).type === 'application/pdf'
  ) {
    return <Pdf source={item.file as PickPhotoOrDocumentResult} style={styles.document} noModal />;
  } else if (item.file && typeof item.file === 'object') {
    const file = item.file as ImageURISource;

    // If photo is a File, we compute the uri as base64
    let photoUri: string | null = null;
    if (file && typeof file === 'object' && file.uri) {
      photoUri = file.uri;
    } else if (file instanceof File) {
      photoUri = URL.createObjectURL(file);
    }

    return (
      <Image
        source={photoUri ? { uri: photoUri } : (item.file as ImageURISource)}
        style={[styles.image, noBorderRadius ? {} : styles.borderRadius]}
        resizeMode="cover"
      />
    );
  } else if (item.documentId) {
    if (noInteractions) {
      return <Pdf source={getDocumentURI(item.documentId)} style={styles.document} noModal />;
    } else {
      const hasPreviousPage = pdfPage > 0;
      const hasNextPage = pdfPage < pdfTotalPage - 1;

      return (
        <TouchableOpacity style={styles.document} onPress={() => item.imageId && setZoomedIndex(index)}>
          {pdfRef.current && pdfTotalPage > 1 && (
            <>
              <TouchableOpacity
                onPress={pdfRef.current?.goToPreviousPage}
                style={[
                  styles.topButton,
                  { left: (width - 30) / 2 },
                  styles.insideButton,
                  !hasPreviousPage ? styles.disabled : undefined,
                ]}
                disabled={!hasPreviousPage}
              >
                <FontAwesome6 name="chevron-up" color={DARK_GRAY} size={30} />
              </TouchableOpacity>
              <TouchableOpacity
                onPress={pdfRef.current?.goToNextPage}
                style={[
                  styles.bottomButton,
                  { left: (width - 30) / 2 },
                  styles.insideButton,
                  !hasNextPage ? styles.disabled : undefined,
                ]}
                disabled={!hasNextPage}
              >
                <FontAwesome6 name="chevron-down" color={DARK_GRAY} size={30} />
              </TouchableOpacity>
            </>
          )}
          <Pdf
            ref={pdfRef}
            source={getDocumentURI(item.documentId)}
            style={Object.assign({}, { width, height })}
            noModal
            onPreviewPageChange={setPdfPage}
            onTotalPageChange={setPdfTotalPage}
          />
        </TouchableOpacity>
      );
    }
  } else if (item.imageId) {
    if (noInteractions) {
      return (
        <Image source={getPictureURI(item.imageId)} style={[styles.image, noBorderRadius ? {} : styles.borderRadius]} />
      );
    } else {
      return (
        <TouchableOpacity
          onPress={() => item.imageId && setZoomedIndex(index)}
          style={[styles.image, noBorderRadius ? {} : styles.borderRadius]}
        >
          <Image source={getPictureURI(item.imageId)} style={styles.image} />
        </TouchableOpacity>
      );
    }
  } else {
    return <View />;
  }
}

export function ContributionPhotoListItemZoomed({
  item,
  width,
  height,
  selected,
  resizeMode,
  zoomEnabled,
}: Omit<ContributionPhotoListItemProps, 'noInteractions' | 'noBorderRadius' | 'setZoomedIndex'> & {
  resizeMode?: ImageResizeMode | undefined;
  zoomEnabled?: boolean;
}) {
  const pdfRef = useRef<PdfRef>(null);
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfTotalPage, setPdfTotalPage] = useState(1);

  if (item.videoUrl) {
    if (!selected) {
      for (const mediaName in MediasInfos) {
        const [, url] = SplitMedia(item.videoUrl);
        const mediaInfo = MediasInfos[mediaName];
        if (mediaInfo.isMedia(url)) {
          const mediaLogo = mediaInfo.logo;

          return (
            <View style={styles.image}>
              <Image source={{ uri: mediaInfo.imagePreview(url) }} style={styles.image} />
              {!!mediaLogo && <Image source={mediaLogo as ImageRequireSource} style={styles.mediaLogo} />}
            </View>
          );
        }
      }

      return <View />;
    } else {
      return (
        <View style={styles.video}>
          <Media media={item.videoUrl} width={width} height={height} />
        </View>
      );
    }
  } else if (
    item.file &&
    typeof item.file === 'object' &&
    (item.file as PickPhotoOrDocumentResult).type === 'application/pdf'
  ) {
    const wrappedContent = (
      <View style={styles.documentZoomedContainer}>
        <Pdf source={item.file as PickPhotoOrDocumentResult} style={styles.document} noModal fit="page" />
      </View>
    );

    if (zoomEnabled) {
      return <ZoomableWeb>{wrappedContent}</ZoomableWeb>;
    } else {
      return wrappedContent;
    }
  } else if (item.file && typeof item.file === 'object') {
    const file = item.file as ImageURISource;

    // If photo is a File, we compute the uri as base64
    let photoUri: string | null = null;
    if (file && typeof file === 'object' && file.uri) {
      photoUri = file.uri;
    } else if (file instanceof File) {
      photoUri = URL.createObjectURL(file);
    }

    const wrappedContent = (
      <Image
        source={photoUri ? { uri: photoUri } : (item.file as ImageURISource)}
        style={styles.image}
        resizeMode={resizeMode || 'cover'}
      />
    );

    if (zoomEnabled) {
      return <ZoomableWeb>{wrappedContent}</ZoomableWeb>;
    } else {
      return wrappedContent;
    }
  } else if (item.documentId) {
    const hasPreviousPage = pdfPage > 0;
    const hasNextPage = pdfPage < pdfTotalPage - 1;

    const wrappedContent = (
      <Pdf
        ref={pdfRef}
        source={getDocumentURI(item.documentId)}
        style={Object.assign({}, { width, height })}
        noModal
        fit="page"
        onPreviewPageChange={setPdfPage}
        onTotalPageChange={setPdfTotalPage}
      />
    );

    return (
      <View style={styles.documentZoomedContainer}>
        {pdfRef.current && pdfTotalPage > 1 && (
          <>
            <TouchableOpacity
              onPress={pdfRef.current?.goToPreviousPage}
              style={[
                styles.topButton,
                { left: (width - 30) / 2 },
                styles.insideButton,
                !hasPreviousPage ? styles.disabled : undefined,
              ]}
              disabled={!hasPreviousPage}
            >
              <FontAwesome6 name="chevron-up" color={DARK_GRAY} size={30} />
            </TouchableOpacity>
            <TouchableOpacity
              onPress={pdfRef.current?.goToNextPage}
              style={[
                styles.bottomButton,
                { left: (width - 30) / 2 },
                styles.insideButton,
                !hasNextPage ? styles.disabled : undefined,
              ]}
              disabled={!hasNextPage}
            >
              <FontAwesome6 name="chevron-down" color={DARK_GRAY} size={30} />
            </TouchableOpacity>
          </>
        )}
        {zoomEnabled ? <ZoomableWeb>{wrappedContent}</ZoomableWeb> : wrappedContent}
      </View>
    );
  } else if (item.imageId) {
    const wrappedContent = (
      <Image source={getPictureURI(item.imageId)} style={styles.image} resizeMode={resizeMode || 'cover'} />
    );

    if (zoomEnabled) {
      return <ZoomableWeb>{wrappedContent}</ZoomableWeb>;
    } else {
      return wrappedContent;
    }
  } else {
    return <View />;
  }
}

const styles = StyleSheet.create({
  defaultImage: {
    marginBottom: 10,
    borderRadius: 20,
  },
  addContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  itemContainer: {
    flex: 1,
  },
  mediaLogo: { position: 'absolute', width: 50, height: 50, right: 10, bottom: 0 },
  video: {
    flex: 1,
    overflow: 'hidden',
  },
  image: {
    flex: 1,
  },
  document: {
    flex: 1,
  },
  documentZoomedContainer: {
    marginBottom: 72,
  },
  topButton: {
    position: 'absolute',
    top: 30,
    zIndex: 10,
    width: 34,
    height: 34,
    justifyContent: 'center',
    alignItems: 'center',
  },
  bottomButton: {
    position: 'absolute',
    bottom: 30,
    zIndex: 10,
    width: 34,
    height: 34,
    justifyContent: 'center',
    alignItems: 'center',
  },
  insideButton: {
    backgroundColor: WHITE,
    borderRadius: 8,
    borderColor: DARK_GRAY,
    borderWidth: 1,
  },
  prevButton: {
    position: 'absolute',
    left: 30,
    zIndex: 10,
    width: 34,
    height: 34,
    justifyContent: 'center',
    alignItems: 'center',
  },
  nextButton: {
    position: 'absolute',
    right: 30,
    zIndex: 10,
    width: 34,
    height: 34,
    justifyContent: 'center',
    alignItems: 'center',
  },
  disabled: {
    opacity: 0.5,
  },
  borderRadius: {
    borderRadius: 20,
    overflow: 'hidden',
  },
  modal: {
    width: '100%',
    maxWidth: !isMobile() ? 700 : undefined,
    overflow: 'hidden',
    flex: 1,
    marginVertical: !isMobile() ? 20 : 0,
    backgroundColor: isMobile() ? 'black' : BACKGROUND_COLOR,
  },
  modalContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalItemContainer: {},
  modalItemLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  modalItemSubcontainer: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
  },
});
